import {afterNextRender, ChangeDetectionStrategy, Component, Injector, Input, inject,} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {BaseComponent, ListFilterService} from 'compob2b-libs';
import {debounceTime, filter, take, takeUntil, tap} from 'rxjs/operators';
import {combineLatest, Subscription} from "rxjs";
import { FormlyModule } from '@ngx-formly/core';
import { NbButtonModule, NbSpinnerModule } from '@nebular/theme';
import { AsyncPipe } from '@angular/common';
import {navigationSnapshot$, queryKey} from '../shared/routing/route-state.stream';
import {catalogIdFromPath} from '../shared/routing/url-builder';

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormlyModule, NbButtonModule, NbSpinnerModule, AsyncPipe]
})
export class FilterComponent extends BaseComponent {
  @Input() filter: ListFilterService;

  formFilter = new UntypedFormGroup({});
  private httpSubscription: Subscription;
  private readonly injector = inject(Injector);

  constructor(protected readonly route: ActivatedRoute) {
    super();

    // get-filter-fields грузим лениво: только когда фильтр-панель открыта ИЛИ мы
    // внутри категории. На дашборде и прочих страницах с закрытой панелью и без
    // категории запрос не нужен — раньше всё равно летел бессмысленный
    // get-filter-fields/0. afterNextRender держит вне SSR + после first paint.
    // Подписка живёт под takeUntil — реагирует и на открытие панели, и на смену
    // категории (та теперь в ПУТИ, а не в query — ТЗ §7).
    afterNextRender(() => {
      // Категория теперь в ПУТИ (ТЗ §7), фильтры остались в query — потребителю нужны
      // ОБА, причём согласованно, поэтому читаем снапшот после навигации (общий
      // `route-state.stream.ts`). Ключ дедупликации включает и query: версия, которая
      // отбрасывала повтор «по категории», скрыла от панели применение фильтра, и
      // фасеты (`get-filter-fields`) перестали пересчитываться.
      const routeState$ = navigationSnapshot$(
        this.router,
        () => {
          const snapshot = this.route.snapshot;
          return {
            catalogId: catalogIdFromPath(this.router.url),
            // `paramsToHttp` (база) принимает `ParamMap`, ключ дедупликации считается по
            // объектной форме — обе из одного снапшота, поэтому расходиться не могут.
            query: snapshot.queryParamMap,
            key: queryKey(snapshot.queryParams),
          };
        },
        (a, b) => a.catalogId === b.catalogId && a.key === b.key,
      );

      combineLatest([
        routeState$,
        toObservable(this.filter.isPanelOpen, {injector: this.injector}),
      ]).pipe(
        filter(([{catalogId}, open]) => open || catalogId !== null),
        tap(() => this.loading = true),
        debounceTime(100),
        tap(([{catalogId, query}]) => this.updateFilters(catalogId, query)),
        takeUntil(this.unsubscribe),
      ).subscribe();
    });
  }

  /**
   * @param catalogId категория ИЗ ПУТИ (null — весь каталог). Для бэка это
   *        по-прежнему query-параметр `/product/get-filter-fields`.
   * @param params    query ИЗ ТОГО ЖЕ снапшота, что и категория. Передаётся аргументом,
   *        а не читается здесь из `route.snapshot`: чтение «по факту вызова» после
   *        `debounceTime` даёт значение другого момента времени, и связь «эмиссия →
   *        параметры запроса» перестаёт быть проверяемой.
   */
  updateFilters(catalogId: number | null, params: ParamMap) {
    this.httpSubscription?.unsubscribe();

    this.httpSubscription = this.filter
      .getProductFilter('/product', catalogId ?? 0, this.paramsToHttp(params))
      .pipe(take(1))
      .subscribe(() => {
        this.loading = false;
        this.httpSubscription = null;
        this.cdr.detectChanges();
      });
  }

  get isCatalog(): boolean {
    return this.router.url?.startsWith('/catalog');
  }

  change(): void {
    this.submit();
  }

  /**
   * Применение фильтров. Куда навигировать — зависит от того, где мы стоим, и оба
   * случая обязательны.
   *
   * **В каталоге** команды пустые, а не `['catalog']`: идентификатор категории уехал
   * из query в path (SEO-8 §7), поэтому жёсткий `['catalog']` пересобирал путь с нуля
   * и выбрасывал пользователя из раздела в корень. Ловилось только в браузере: клик по
   * подкатегории приводил на `/catalog`, потому что загрузка новых полей фильтра
   * дергает `change()` → `submit()`.
   *
   * **Вне каталога** (главная и прочие страницы) — наоборот, обязателен `['/catalog']`:
   * там панель показывает кнопку «Найти товары», и её смысл в том, чтобы УЙТИ в каталог
   * с выбранным фильтром. Пустые команды применяли фильтр к текущей странице, человек
   * оставался на месте и без товаров. Это регресс SEO-9 (`fcdc2a93d`): исправляя первый
   * случай, замену команд применили к обоим.
   *
   * `relativeTo` не нужен: с пустыми командами он не влияет, а с абсолютным `/catalog`
   * тем более.
   */
  submit(): void {
    if (!this.formFilter.valid) {
      return;
    }

    const queryParams = this.filter.convertModelFeatures();

    // Пересборка полей сама пишет в модель — formly раскладывает `defaultValue`, и
    // `(modelChange)` не отличает это от клика пользователя. Отличить можно по
    // РЕЗУЛЬТАТУ: эхо пересборки воспроизводит ровно тот query, что уже в адресе, а
    // клик человека всегда даёт другой. Поэтому сравниваем, а не гадаем по таймингу.
    //
    // Предыдущая версия глушила `change()` на всё время запроса — и вместе с эхом
    // съедала настоящие клики, сделанные в это окно: галочка визуально вставала, в
    // адрес не уходила, а пришедшая пересборка её стирала. Ровно то, на что жаловались
    // («быстро щёлкаешь две — остаётся одна»). Проверено в браузере на стенде.
    //
    // Сравнение нужно и само по себе: навигация с тем же query всё равно добавила бы
    // запись в историю, и «назад» вело бы на ту же страницу.
    if (!this.isCatalog || !this.sameAsUrl(queryParams)) {
      this.router.navigate(this.isCatalog ? [] : ['/catalog'], {
        queryParams,
        queryParamsHandling: null
      });
    }
  }

  /** Совпадает ли собранный набор с тем, что уже в адресной строке. */
  private sameAsUrl(queryParams: Record<string, unknown>): boolean {
    const current = this.route.snapshot.queryParams;
    const next = Object.entries(queryParams).filter(([, v]) => v !== null && v !== undefined);

    return next.length === Object.keys(current).length
      && next.every(([k, v]) => String(current[k]) === String(v));
  }

  reset(): void {
    this.filter.reset();
    this.cdr.detectChanges();

    this.router.navigate([], {
      queryParams: {},
      queryParamsHandling: null
    });
  }

}
